import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { FiLoader } from "react-icons/fi";
import { endpoint } from "../../api/endpoint";
import { loginSuccess } from "../../features/Auth/AuthSlice";
import MainLogo from "../../assets/image/logo.png";
import styles from "./Login.module.css";

const normalizeEmail = (email) => String(email || "").trim().toLowerCase();

const getConfiguredTestEmails = () =>
  String(process.env.REACT_APP_TEST_USER_EMAILS || process.env.REACT_APP_TEST_USER_EMAIL || "")
    .split(",")
    .map(normalizeEmail)
    .filter(Boolean);

const isConfiguredTestUser = (email) => {
  const normalizedEmail = normalizeEmail(email);
  if (!normalizedEmail) return false;

  return getConfiguredTestEmails().includes(normalizedEmail);
};

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const handleSendOtp = async (event) => {
    event.preventDefault();
    setError("");
    setMessage("");
    sessionStorage.removeItem("accessToken");

    if (!email.trim()) {
      setError("Please enter a valid email");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${endpoint.API_BASE_URL}${endpoint.SEND_OTP}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "*/*",
          },
          body: JSON.stringify({ email }),
        }
      );

      if (response.ok) {
        setMessage("OTP sent successfully to your email");
        setStep("otp");
        setOtp("");
      } else {
        const errorData = await response.json();
        setError(errorData.message || "Failed to send OTP");
      }
    } catch (err) {
      setError("Network error. Please try again.");
      console.error("Send OTP error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (event) => {
    event.preventDefault();
    setError("");
    setMessage("");

    const trimmedOtp = otp.trim();

    if (!trimmedOtp) {
      setError("Please enter the OTP");
      return;
    }

    if (trimmedOtp.length > 6) {
      setError("OTP cannot be more than 6 characters");
      return;
    }

    const OTP_ALLOWED_REGEX = /^[a-zA-Z0-9@#_-]+$/;
    if (!OTP_ALLOWED_REGEX.test(trimmedOtp)) {
      setError("OTP can only contain letters, numbers, and @, #, _, -");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${endpoint.API_BASE_URL}${endpoint.VERIFY_OTP}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "*/*",
          },
          body: JSON.stringify({ email: email.trim(), otp: trimmedOtp }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        const newAccessToken =
          data?.data?.accessToken ||
          data?.accessToken ||
          data?.data?.token ||
          data?.token;

        if (newAccessToken) {
          sessionStorage.setItem("accessToken", newAccessToken);
        }

        const verifiedEmail = (data?.data?.email || data?.email || email).trim();
        const role = data?.data?.role || data?.role || data?.sRole || "user";
        const iStoreId =
          data?.data?.iStoreId ?? data?.data?.storeId ?? data?.iStoreId ?? data?.storeId ?? null;
        const storeName = data?.storeName ?? data?.sStoreName ?? null;
        const isTestUser = data?.data?.isTestUser ?? data?.isTestUser ?? isConfiguredTestUser(verifiedEmail);
        setMessage("OTP verified successfully!");
        dispatch(
          loginSuccess({
            user: { email: verifiedEmail },
            role,
            token: newAccessToken || "otp-authenticated",
            isTestUser,
            iStoreId,
            storeName,
          })
        );
        setTimeout(() => {
          navigate("/playground");
        }, 1000);
      } else {
        const errorData = await response.json();
        setError(errorData.message || "Invalid OTP");
      }
    } catch (err) {
      setError("Network error. Please try again.");
      console.error("Verify OTP error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToEmail = () => {
    setStep("email");
    setOtp("");
    setError("");
    setMessage("");
  };

  return (
    <main className={styles.loginPageWrapper}>
      <div className={styles.row}>
        <div className={styles.colmd8}>
          <div className={styles.maineformbox}>
            <section className={styles.newloginBox}>
              <div className={styles.maineweblogo}>
                <img src={MainLogo} alt="Logo" className={styles.maineweblogoImg} />
              </div>

              <h1 className={styles.loginHeading}>Welcome!</h1>
              <small className={styles.small}>
                {step === "email"
                  ? "Continue to Login with Email and OTP!"
                  : `Enter the OTP sent to ${email}`}
              </small>

              {error && <small className={styles.errorMessage}>{error}</small>}
              {message && <small className={styles.successMessage}>{message}</small>}

              <div className={`${styles.mt4} ${styles.frontlogin}`}>
                <form onSubmit={step === "email" ? handleSendOtp : handleVerifyOtp}>
                  {step === "email" ? (
                    <div className={styles.formLabel}>
                      <label htmlFor="email">Email Address</label>
                      <input
                        id="email"
                        type="email"
                        value={email}
                        className={styles.formControl}
                        onChange={(event) => setEmail(event.target.value)}
                        placeholder="Enter your email"
                        autoComplete="email"
                        disabled={loading}
                        required
                      />
                    </div>
                  ) : (
                    <>
                      <div className={styles.formLabel}>
                        <label htmlFor="otp">One-Time Password</label>
                        <input
                          id="otp"
                          type="text"
                          value={otp}
                          className={styles.formControl}
                          onChange={(event) => setOtp(event.target.value)}
                          placeholder="Enter 6-digit OTP"
                          autoComplete="one-time-code"
                          inputMode="text"
                          maxLength="6"
                          disabled={loading}
                          required
                        />
                      </div>

                      <div className={styles.formActionsInline}>
                        <button
                          type="button"
                          className={styles.linkButton}
                          onClick={handleBackToEmail}
                          disabled={loading}
                        >
                          Back
                        </button>
                        <button
                          type="button"
                          className={styles.linkButton}
                          onClick={handleSendOtp}
                          disabled={loading}
                        >
                          Resend OTP
                        </button>
                      </div>
                    </>
                  )}

                  <button className={styles.signInButton} type="submit" disabled={loading}>
                    {loading ? (
                      <FiLoader className={styles.loaderIcon} />
                    ) : step === "email" ? (
                      "Send OTP"
                    ) : (
                      "Verify OTP"
                    )}
                  </button>
                </form>
              </div>
            </section>
          </div>
        </div>
      </div>
    </main>
  );
};

export default Login;
